// ===========================================================================
// ReHome — Supabase Data Hooks
// Generic hooks for fetching data with loading/error states.
// Falls back to mock data when Supabase is unavailable.
// ===========================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from './supabase';
import type { Animal, Shelter, DonationCampaign, AppNotification, FinancialTransaction, AdopterProfile, ShelterAdoptionPolicy, AnimalIntakeRecord, AnimalMedicalEvent, AnimalMedicalReminder, ShelterNotificationSettings } from './types';

const isDev = () => {
  try { return (import.meta as any).env?.DEV; } catch { return false; }
};

// ─── Case conversion ────────────────────────────────────────────────────────
// PostgREST/Supabase returns raw DB column names (snake_case). Our TypeScript
// types use camelCase (see types.ts). There is NO automatic conversion done
// by supabase-js, so every row coming back from a query must be converted
// here — otherwise fields like `shelter_id` never populate `animal.shelterId`
// and all client-side filtering by shelter silently returns nothing.
const snakeToCamel = (key: string): string =>
  key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

export function toCamelCase<T = any>(input: any): T {
  if (Array.isArray(input)) {
    return input.map(item => toCamelCase(item)) as any;
  }
  if (input !== null && typeof input === 'object' && !(input instanceof Date)) {
    const out: Record<string, any> = {};
    for (const [key, value] of Object.entries(input)) {
      out[snakeToCamel(key)] = toCamelCase(value);
    }
    return out as T;
  }
  return input;
}

// ─── Generic hook ───────────────────────────────────────────────────────────

interface QueryState<T> {
  data: T[];
  loading: boolean;
  error: string | null;
}

function useSupabaseQuery<T>(
  tableName: string,
  queryBuilder: (query: ReturnType<typeof supabase.from>) => any,
  mockFallback?: T[],
  deps: unknown[] = []
): QueryState<T> & { refetch: () => void } {
  const [state, setState] = useState<QueryState<T>>({
    data: mockFallback || [],
    loading: true,
    error: null,
  });
  // Use refs for mockFallback/queryBuilder and retry tracking to avoid stale
  // closures: the builder closure changes every render (it captures params
  // like shelterId), but we only want to re-run the query when `deps` change.
  const mockRef = useRef(mockFallback);
  mockRef.current = mockFallback;
  const builderRef = useRef(queryBuilder);
  builderRef.current = queryBuilder;
  const retryRef = useRef(false);

  const fetch = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setState({ data: mockRef.current || [], loading: false, error: null });
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const query = supabase.from(tableName);
      const built = builderRef.current(query);
      const { data, error } = await built;

      if (error) throw error;

      // If Supabase is configured and returns data (even empty), use it —
      // converted from snake_case DB columns to camelCase for our types.
      // Only fall back to mock when Supabase is NOT configured at all.
      if (data !== null && data !== undefined) {
        setState({ data: toCamelCase<T[]>(data), loading: false, error: null });
      } else {
        setState({ data: [], loading: false, error: null });
      }
    } catch (err: any) {
      if (isDev()) console.warn(`[useSupabaseQuery] ${tableName}:`, err.message);
      // Supabase IS configured but the request failed (network blip, RLS, etc).
      // Never silently swap in mock data here — that would hide real errors
      // behind fake-looking data. Keep whatever we had and surface the error.
      setState(prev => ({ ...prev, loading: false, error: err.message }));
      if (!retryRef.current) {
        retryRef.current = true;
        setTimeout(() => fetch(), 2000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, ...deps]); // deps: query parameters (e.g. shelterId) that must trigger a refetch

  useEffect(() => { fetch(); }, [fetch]);

  return { ...state, refetch: fetch };
}

// ─── Specific entity hooks ──────────────────────────────────────────────────

/** Fetch all animals with their shelter info (public RLS). */
export function useAnimals(): QueryState<Animal> & { refetch: () => void } {
  return useSupabaseQuery<Animal>(
    'animals',
    q => q.select('*').order('intake_date', { ascending: false }),
    MOCK_ANIMALS
  );
}

/**
 * Default cap for the "available animals" browse query. This is a safety
 * ceiling against pulling an unbounded table into the browser — NOT a page
 * size. It used to be 20, which silently truncated the Gazdikereső list (and
 * the Smart Match candidate pool) the moment a shelter had more than 20
 * adoptable animals: the DB held 28 `Available` rows but only 20 could ever
 * be browsed, filtered or matched. Keep it comfortably above the realistic
 * platform-wide count; callers that only need a handful pass their own limit.
 */
const AVAILABLE_ANIMALS_LIMIT = 500;

/**
 * Fetch available animals for browsing.
 * @param limit optional cap — defaults to AVAILABLE_ANIMALS_LIMIT.
 */
export function useAvailableAnimals(limit: number = AVAILABLE_ANIMALS_LIMIT): QueryState<Animal> & { refetch: () => void } {
  return useSupabaseQuery<Animal>(
    'animals',
    q => q.select('*').eq('status', 'Available').order('intake_date', { ascending: false }).limit(limit),
    MOCK_ANIMALS.filter(a => a.status === 'Available'),
    [limit]
  );
}

/**
 * Fetch all animals for ONE shelter (server-side filtered). Use this instead
 * of `useAnimals()` + client-side `.filter(a => a.shelterId === shelterId)`
 * — the latter pulls every animal on the whole platform into the browser
 * just to discard everyone else's rows.
 */
export function useShelterAnimals(shelterId: string | undefined): QueryState<Animal> & { refetch: () => void } {
  return useSupabaseQuery<Animal>(
    'animals',
    q => {
      let builder = q.select('*').order('intake_date', { ascending: false });
      if (shelterId) builder = builder.eq('shelter_id', shelterId);
      return builder;
    },
    MOCK_ANIMALS,
    [shelterId]
  );
}

/**
 * Per-shelter animal counts, computed in SQL (get_shelter_animal_counts RPC)
 * instead of fetching every animal row on the platform to count them
 * client-side. Used by GlobalDashboardPage/SuperAdminPage.
 */
export function useShelterAnimalCounts(): { counts: Map<string, number>; total: number; loading: boolean; error: string | null } {
  const [counts, setCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setCounts(new Map(MOCK_ANIMALS.reduce((m, a) => m.set(a.shelterId, (m.get(a.shelterId) || 0) + 1), new Map<string, number>())));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: err } = await supabase.rpc('get_shelter_animal_counts');
        if (err) throw err;
        if (!cancelled) {
          setCounts(new Map((data || []).map((r: any) => [r.shelter_id, Number(r.animal_count)])));
        }
      } catch (err: any) {
        if (isDev()) console.warn('[useShelterAnimalCounts]', err.message);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const total = Array.from(counts.values()).reduce((s, n) => s + n, 0);
  return { counts, total, loading, error };
}

/**
 * Platform-wide species breakdown, computed in SQL (get_species_breakdown
 * RPC) instead of fetching every animal row to tally species client-side.
 * Used by GlobalDashboardPage.
 */
export function useSpeciesBreakdown(): { data: Array<{ species: string; count: number }>; loading: boolean; error: string | null } {
  const [data, setData] = useState<Array<{ species: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      const bySpecies = new Map<string, number>();
      MOCK_ANIMALS.forEach(a => bySpecies.set(a.species, (bySpecies.get(a.species) || 0) + 1));
      setData(Array.from(bySpecies.entries()).map(([species, count]) => ({ species, count })));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: rows, error: err } = await supabase.rpc('get_species_breakdown');
        if (err) throw err;
        if (!cancelled) {
          setData((rows || []).map((r: any) => ({ species: r.species, count: Number(r.animal_count) })));
        }
      } catch (err: any) {
        if (isDev()) console.warn('[useSpeciesBreakdown]', err.message);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { data, loading, error };
}

/**
 * Platform-wide status breakdown (get_status_breakdown RPC).
 *
 * Ugyanaz az elv, mint a faj szerinti bontásnál: az összegzés a szerveren
 * történik, mert a kliensoldali megszámoláshoz minden állat sorát le kellene
 * tölteni. A válasz legfeljebb néhány sor, akárhány állatról van szó.
 */
export function useStatusBreakdown(): { data: Array<{ status: string; count: number }>; loading: boolean; error: string | null } {
  const [data, setData] = useState<Array<{ status: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      const byStatus = new Map<string, number>();
      MOCK_ANIMALS.forEach(a => byStatus.set(a.status, (byStatus.get(a.status) || 0) + 1));
      setData(Array.from(byStatus.entries()).map(([status, count]) => ({ status, count })));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: rows, error: err } = await supabase.rpc('get_status_breakdown');
        if (err) throw err;
        if (!cancelled) {
          setData((rows || []).map((r: any) => ({ status: r.status, count: Number(r.animal_count) })));
        }
      } catch (err: any) {
        if (isDev()) console.warn('[useStatusBreakdown]', err.message);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { data, loading, error };
}

/**
 * Egyetlen menhely adatai azonosító alapján.
 *
 * A vezérlőpult csak a nevet akarja kiírni — ehhez az összes menhely
 * letöltése (useShelters) fölösleges, és a hálózat növekedésével egyre
 * drágább lenne.
 */
export function useShelter(shelterId: string | undefined): { shelter: Shelter | null; loading: boolean } {
  const [shelter, setShelter] = useState<Shelter | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!shelterId) { setShelter(null); setLoading(false); return; }

    if (!isSupabaseConfigured()) {
      setShelter(MOCK_SHELTERS.find(s => s.id === shelterId) || null);
      setLoading(false);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase.from('shelters').select('*').eq('id', shelterId).single();
        if (error) throw error;
        if (!cancelled) setShelter(data ? toCamelCase<Shelter>(data) : null);
      } catch (err: any) {
        if (isDev()) console.warn('[useShelter]', err.message);
        if (!cancelled) setShelter(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shelterId]);

  return { shelter, loading };
}

/**
 * Egy állathoz tartozó pénzügyi tételek.
 *
 * A `financial_transactions.animal_id` indexelt, tehát ez pontszerű
 * lekérdezés — nem a menhely teljes pénzügyi listáját szűri a kliens.
 */
export function useAnimalTransactions(animalId: string | undefined): {
  transactions: FinancialTransaction[];
  loading: boolean;
} {
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!animalId || !isSupabaseConfigured()) { setTransactions([]); setLoading(false); return; }

    setLoading(true);
    supabase
      .from('financial_transactions')
      .select('*')
      .eq('animal_id', animalId)
      .order('date', { ascending: false })
      .limit(100)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          if (isDev()) console.warn('[useAnimalTransactions]', error.message);
          setTransactions([]);
        } else {
          setTransactions(toCamelCase<FinancialTransaction[]>(data || []));
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [animalId]);

  return { transactions, loading };
}

/** Fetch a single animal by ID. Returns the animal or null. */
export function useAnimal(animalId: string | undefined): { animal: Animal | null; loading: boolean; error: string | null; refetch: () => void } {
  const [animal, setAnimal] = useState<Animal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!animalId) {
      setAnimal(null);
      setLoading(false);
      return;
    }

    if (!isSupabaseConfigured()) {
      const mock = MOCK_ANIMALS.find(a => a.id === animalId) ?? null;
      setAnimal(mock);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Embed adoption leads (aliased to `leads` to match the Animal type).
      // RLS filters the embedded rows: staff see their shelter's leads,
      // adopters see their own, anon sees none.
      // The !animal_id hint is required: migration 023 added animals.lead_id,
      // so two FK paths exist between animals and adoption_leads and
      // PostgREST refuses to embed without disambiguation.
      const { data, error: err } = await supabase
        .from('animals')
        .select('*, leads:adoption_leads!animal_id(*)')
        .eq('id', animalId)
        .maybeSingle();

      if (err) throw err;
      setAnimal(data ? toCamelCase<Animal>(data) : null);
    } catch (err: any) {
      if (isDev()) console.warn('[useAnimal]', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { animal, loading, error, refetch: fetch };
}

/** Fetch all shelters (public RLS). */
export function useShelters(): QueryState<Shelter> & { refetch: () => void } {
  return useSupabaseQuery<Shelter>(
    'shelters',
    q => q.select('*').order('name'),
    MOCK_SHELTERS
  );
}

/** Fetch active donation campaigns (optionally scoped to one shelter). */
export function useCampaigns(shelterId?: string): QueryState<DonationCampaign> & { refetch: () => void } {
  return useSupabaseQuery<DonationCampaign>(
    'donation_campaigns',
    q => {
      let builder = q.select('*').eq('is_active', true).order('raised_amount', { ascending: false });
      if (shelterId) builder = builder.eq('shelter_id', shelterId);
      return builder;
    },
    MOCK_CAMPAIGNS,
    [shelterId]
  );
}

/** Fetch notifications for a shelter. */
export function useNotifications(shelterId?: string): QueryState<AppNotification> & { refetch: () => void } {
  return useSupabaseQuery<AppNotification>(
    'notifications',
    q => {
      let builder = q.select('*').order('created_at', { ascending: false }).limit(50);
      if (shelterId) builder = builder.eq('shelter_id', shelterId);
      return builder;
    },
    MOCK_NOTIFICATIONS,
    [shelterId]
  );
}

/**
 * Az örökbefogadó SAJÁT, már elbírált igénylései — az elutasítottakkal együtt.
 *
 * ── Miért nem elég az állatlista ───────────────────────────────────────────
 * A "Saját állataim" azokat az állatokat mutatja, ahol `animals.adopter_id`
 * a bejelentkezett felhasználó. Elutasításkor viszont a `review_adoption`
 * pontosan ezt a mezőt üríti ki (az állat visszakerül a gazdikeresők közé) —
 * vagyis az elutasított igénylés, és vele a menhely indoklása, ugyanabban a
 * pillanatban tűnik el a felületről, amikor megszületik. Az indoklás így
 * sosem jutna el ahhoz, akinek szól.
 *
 * Ez a lekérdezés ezért az `adoption_leads` felől néz, amit az RLS a saját
 * igénylésekre szűkít. Az időkorlát azért van, hogy egy régi elutasítás ne
 * álljon örökre a felület tetején.
 */
export function useMyAdoptionDecisions(withinDays = 60): {
  decisions: Array<{
    id: string;
    animalId: string;
    animalName: string;
    animalImageUrl: string | null;
    status: string;
    reviewNote: string | null;
    reviewedAt: string | null;
  }>;
  loading: boolean;
  refetch: () => void;
} {
  const [decisions, setDecisions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!isSupabaseConfigured()) { setDecisions([]); setLoading(false); return; }

    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (!userId) { setDecisions([]); setLoading(false); return; }

    const since = new Date();
    since.setDate(since.getDate() - withinDays);

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('adoption_leads')
        // A kapcsolatot NÉVVEL kell megadni: az `animals` és az
        // `adoption_leads` között két idegen kulcs is fut (a lead az
        // állatra, és az állat a nyertes leadre), amiből a PostgREST nem
        // tudja kitalálni, melyiken keresztül kell csatolni.
        .select('id, animal_id, status, review_note, rejection_reason, reviewed_at, animal:animals!adoption_leads_animal_id_fkey(name, image_url)')
        .eq('adopter_id', userId)
        .eq('status', 'Rejected')
        .gte('reviewed_at', since.toISOString())
        .order('reviewed_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setDecisions((data || []).map((row: any) => ({
        id: row.id,
        animalId: row.animal_id,
        animalName: row.animal?.name ?? '',
        animalImageUrl: row.animal?.image_url ?? null,
        status: row.status,
        // A `rejection_reason` a 045 előtti sorok miatt marad tartalékként.
        reviewNote: row.review_note ?? row.rejection_reason ?? null,
        reviewedAt: row.reviewed_at ?? null,
      })));
    } catch (err: any) {
      if (isDev()) console.warn('[useMyAdoptionDecisions]', err.message);
      setDecisions([]);
    } finally {
      setLoading(false);
    }
  }, [withinDays]);

  useEffect(() => { fetch(); }, [fetch]);

  return { decisions, loading, refetch: fetch };
}

/** Mark a notification as read. Returns true on success. */
export async function markNotificationRead(notificationId: string): Promise<boolean> {
  if (!isSupabaseConfigured()) return true;
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);
  if (error) {
    if (isDev()) console.warn('[markNotificationRead]', error.message);
    return false;
  }
  return true;
}

/**
 * Snoozes a notification ("Később") — it reappears once `snoozedUntil` passes.
 * Shelter-wide, like `is_read`: whichever staff member snoozes it, it's
 * hidden for everyone in the shelter until the snooze window elapses.
 */
export async function snoozeNotification(notificationId: string, snoozeMinutes: number): Promise<boolean> {
  if (!isSupabaseConfigured()) return true;
  const snoozedUntil = new Date(Date.now() + snoozeMinutes * 60_000).toISOString();
  const { error } = await supabase
    .from('notifications')
    .update({ snoozed_until: snoozedUntil })
    .eq('id', notificationId);
  if (error) {
    if (isDev()) console.warn('[snoozeNotification]', error.message);
    return false;
  }
  return true;
}

/** Fetch financial transactions (ReHome-only, optionally scoped to one shelter). */
export function useFinancialTransactions(shelterId?: string): QueryState<FinancialTransaction> & { refetch: () => void } {
  return useSupabaseQuery<FinancialTransaction>(
    'financial_transactions',
    q => {
      let builder = q.select('*').order('created_at', { ascending: false }).limit(100);
      if (shelterId) builder = builder.eq('shelter_id', shelterId);
      return builder;
    },
    MOCK_TRANSACTIONS,
    [shelterId]
  );
}

/**
 * Network-wide ReHome balance (Income - Expense) via the get_network_balance RPC.
 * RLS applies: shelter staff see their own shelter's balance, super_admin sees all.
 */
export function useNetworkBalance(): { balance: number | null; loading: boolean; error: string | null } {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: err } = await supabase.rpc('get_network_balance');
        if (err) throw err;
        if (!cancelled) setBalance(typeof data === 'number' ? data : Number(data) || 0);
      } catch (err: any) {
        if (isDev()) console.warn('[useNetworkBalance]', err.message);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { balance, loading, error };
}

/** Fetch animals owned by the current user (Adopted + AdoptionPending) with post-adoption updates. */
export function useAdoptedAnimals(): QueryState<Animal> & { refetch: () => void } {
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Animal[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: userData }) => {
      setUserId(userData?.user?.id ?? null);
      if (!userData?.user?.id) setLoading(false);
    });
  }, []);

  const fetch = useCallback(async () => {
    if (!userId) return;
    if (!isSupabaseConfigured()) {
      setData(MOCK_ADOPTED_ANIMALS);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: rows, error: err } = await supabase
        .from('animals')
        .select('*, post_adoption_updates(*)')
        .eq('adopter_id', userId)
        .order('adoption_date', { ascending: false })
        .limit(30);

      if (err) throw err;
      setData(rows ? toCamelCase<Animal[]>(rows) : []);
    } catch (err: any) {
      if (isDev()) console.warn('[useAdoptedAnimals]', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { if (userId) fetch(); }, [fetch]);

  return { data: data ?? [], loading, error, refetch: fetch };
}

/**
 * Fetch a shelter's adopted animals with post-adoption updates (admin side).
 * Adopter names are resolved from the linked (Approved) adoption lead,
 * because user_profiles of adopters are not readable by shelter staff (RLS).
 */
export function useShelterAdoptedAnimals(shelterId: string | undefined): QueryState<Animal> & { refetch: () => void } {
  const [data, setData] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!shelterId) { setData([]); setLoading(false); return; }
    if (!isSupabaseConfigured()) {
      setData(MOCK_ADOPTED_ANIMALS);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from('animals')
        .select('*, post_adoption_updates(*), leads:adoption_leads!animal_id(id, name, status)')
        .eq('shelter_id', shelterId)
        .eq('status', 'Adopted')
        .order('adoption_date', { ascending: false })
        .limit(2000);

      if (err) throw err;
      const animals = toCamelCase<Animal[]>(rows || []);
      // Resolve adopter name from the approved lead (if any)
      for (const a of animals) {
        const approved = (a.leads || []).find(l => l.status === 'Approved');
        if (approved) a.adopterName = approved.name;
      }
      setData(animals);
    } catch (err: any) {
      if (isDev()) console.warn('[useShelterAdoptedAnimals]', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [shelterId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ═══════════════════════════════════════════════════════════════════════════
// Server-side Full-Text Search (Hungarian)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Search animals using PostgreSQL full-text search (Hungarian dictionary).
 * Calls the search_animals RPC function. Falls back to client-side filtering.
 */
export async function searchAnimals(queryText: string, limit: number = 20): Promise<Animal[]> {
  if (!queryText.trim()) return [];

  if (!isSupabaseConfigured()) {
    // Fallback: client-side search on mock data
    const q = queryText.toLowerCase();
    return MOCK_ANIMALS.filter(
      a =>
        a.status === 'Available' &&
        ((a.name || '').toLowerCase().includes(q) ||
         (a.breed || '').toLowerCase().includes(q) ||
         (a.bio || '').toLowerCase().includes(q) ||
         (a.traits || []).some(t => t.toLowerCase().includes(q)))
    ).slice(0, limit);
  }

  try {
    const { data, error } = await supabase.rpc('search_animals', {
      query_text: queryText,
      limit_rows: limit,
    });

    if (error) throw error;
    return toCamelCase<Animal[]>(data) || [];
  } catch (err: any) {
    console.warn('[searchAnimals] RPC fallback:', err.message);
    // Fallback to basic text search via Supabase ilike
    try {
      const pattern = `%${queryText}%`;
      const { data } = await supabase
        .from('animals')
        .select('*')
        .eq('status', 'Available')
        .or(`name.ilike.${pattern},breed.ilike.${pattern},bio.ilike.${pattern}`)
        .limit(limit);
      return toCamelCase<Animal[]>(data) || [];
    } catch {
      if (isDev()) console.warn('[searchAnimals] All search methods failed');
      return [];
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Adopter Profile Hook
// ═══════════════════════════════════════════════════════════════════════════

export const useAdopterProfile = () => {
  const [data, setData] = useState<AdopterProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        const { data: profile, error: err } = await supabase
          .from('adopter_profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (err) throw err;
        setData(profile ? toCamelCase<AdopterProfile>(profile) : null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  return { data, loading, error };
};

// ═══════════════════════════════════════════════════════════════════════════
// Shelter Adoption Policy Hook
// ═══════════════════════════════════════════════════════════════════════════

export const useShelterPolicy = (shelterId: string | null | undefined) => {
  const [data, setData] = useState<ShelterAdoptionPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shelterId || !isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    const fetchPolicy = async () => {
      setLoading(true);
      try {
        const { data: policy, error: err } = await supabase
          .from('shelter_adoption_policies')
          .select('*')
          .eq('shelter_id', shelterId)
          .maybeSingle();

        if (err) throw err;
        setData(policy ? toCamelCase<ShelterAdoptionPolicy>(policy) : null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, [shelterId]);

  return { data, loading, error };
};

// ═══════════════════════════════════════════════════════════════════════════
// Animal Intake Record Hook (staff-only, never used by platform-frontend)
// ═══════════════════════════════════════════════════════════════════════════

export const useAnimalIntakeRecord = (animalId: string | null | undefined) => {
  const [data, setData] = useState<AnimalIntakeRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecord = useCallback(async () => {
    if (!animalId || !isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: record, error: err } = await supabase
        .from('animal_intake_records')
        .select('*')
        .eq('animal_id', animalId)
        .maybeSingle();

      if (err) throw err;
      setData(record ? toCamelCase<AnimalIntakeRecord>(record) : null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  return { data, loading, error, refetch: fetchRecord };
};

// ═══════════════════════════════════════════════════════════════════════════
// Animal Medical Events Hook (staff-only, never used by platform-frontend)
// ═══════════════════════════════════════════════════════════════════════════

export const useAnimalMedicalEvents = (animalId: string | null | undefined) => {
  const [data, setData] = useState<AnimalMedicalEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    if (!animalId || !isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: events, error: err } = await supabase
        .from('animal_medical_events')
        .select('*')
        .eq('animal_id', animalId)
        .order('event_date', { ascending: false });

      if (err) throw err;
      setData(events ? events.map(e => toCamelCase<AnimalMedicalEvent>(e)) : []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  return { data, loading, error, refetch: fetchEvents };
};

// ═══════════════════════════════════════════════════════════════════════════
// Medical form suggestion pools
// ---------------------------------------------------------------------------
// Az "Egészségügyi adatok" űrlap két szabad szöveges mezője (Állatorvos,
// Megnevezés) eddig teljesen üres volt: ugyanaz a néhány érték került be újra
// és újra, minden alkalommal más írásmóddal. Mindkét lista a MÁR RÖGZÍTETT
// adatokból épül — nincs beégetett szótár, a felhasználók alakítják ki.
//
// A két mező szándékosan MÁS hatókörű (l. 039_medical_neutering_and_vocab.sql):
//   • állatorvos → menhelyre szűkítve (személyes adat, más menhelyre nem tartozik)
//   • megnevezés → platform-szintű (szakmai szókincs, épp az a lényege, hogy közös)
// ═══════════════════════════════════════════════════════════════════════════

interface SuggestionPool {
  /** Javaslatok gyakoriság szerint csökkenő sorrendben. */
  options: string[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Közös RPC-alapú javaslatlista-betöltő. */
function useSuggestionPool(
  rpcName: string,
  params: Record<string, unknown> | null,
  valueKey: string,
  deps: unknown[]
): SuggestionPool {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPool = useCallback(async () => {
    if (!params || !isSupabaseConfigured()) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc(rpcName, params);
      if (err) throw err;
      setOptions((data || []).map((row: any) => row[valueKey]).filter(Boolean));
    } catch (err: any) {
      if (isDev()) console.warn(`[useSuggestionPool] ${rpcName}:`, err.message);
      setError(err.message);
      // Üres listával a mező sima szabad szövegként működik tovább — a
      // javaslatok hiánya sosem akadályozhatja meg a rögzítést.
      setOptions([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpcName, valueKey, ...deps]);

  useEffect(() => { fetchPool(); }, [fetchPool]);

  return { options, loading, error, refetch: fetchPool };
}

/** A menhelyen korábban rögzített állatorvosok, gyakoriság szerint. */
export function useShelterVeterinarians(shelterId: string | null | undefined): SuggestionPool {
  return useSuggestionPool(
    'get_shelter_veterinarians',
    shelterId ? { p_shelter_id: shelterId } : null,
    'veterinarian',
    [shelterId]
  );
}

/** Egy esemény-típushoz korábban használt megnevezések, gyakoriság szerint. */
export function useMedicalEventTitles(eventType: string | null | undefined): SuggestionPool {
  return useSuggestionPool(
    'get_medical_event_titles',
    eventType ? { p_event_type: eventType } : null,
    'title',
    [eventType]
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Shelter Notification Settings Hook
// ═══════════════════════════════════════════════════════════════════════════

export const useShelterNotificationSettings = (shelterId: string | null | undefined) => {
  const [data, setData] = useState<ShelterNotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    if (!shelterId || !isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: settings, error: err } = await supabase
        .from('shelter_notification_settings')
        .select('*')
        .eq('shelter_id', shelterId)
        .maybeSingle();

      if (err) throw err;
      setData(settings ? toCamelCase<ShelterNotificationSettings>(settings) : null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [shelterId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  return { data, loading, error, refetch: fetchSettings };
};

// ═══════════════════════════════════════════════════════════════════════════
// Upcoming Medical Reminders — shelter-wide, date-ascending (staff-only)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * All recurring medical events with a next_due_date, across the whole
 * shelter, soonest first — regardless of whether a notification has fired
 * yet for them (unlike useNotifications, which only surfaces events already
 * within their lead time). Joins the animal's name/species in the same query.
 */
export function useUpcomingMedicalReminders(shelterId?: string): QueryState<AnimalMedicalReminder> & { refetch: () => void } {
  return useSupabaseQuery<AnimalMedicalReminder>(
    'animal_medical_events',
    q => {
      let builder = q
        .select('*, animal:animals!inner(id, name, species, shelter_id)')
        .eq('is_recurring', true)
        .not('next_due_date', 'is', null)
        .order('next_due_date', { ascending: true })
        .limit(2000);
      if (shelterId) builder = builder.eq('animal.shelter_id', shelterId);
      return builder;
    },
    [],
    [shelterId]
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MOCK FALLBACK DATA (used when Supabase is empty or unavailable)
// Note: dates are computed at import time (frozen). For long-running apps,
// consider wrapping in getter functions to keep dates current.
// ═══════════════════════════════════════════════════════════════════════════

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };

const MOCK_ANIMALS: Animal[] = [
  { id: 'mock-nje-2', shelterId: 'nje', name: 'Mici', species: 'Macska', breed: 'Európai rövidszőrű', age: 2, gender: 'Nőstény', weightKg: 3.5, traits: ['Barátságos', 'Játékos', 'Nyugodt'], bio: 'Mici egy bújós, kedves cica, aki imádja a simogatást. Csendes otthonba való.', imageUrl: 'https://images.unsplash.com/photo-1573865526739-10659fec78a5?auto=format&fit=crop&q=80&w=800', gallery: [], intakeDate: daysAgo(75), intakeType: 'Stray', foundLocation: null, strayHoldEndDate: null, status: 'Available', microchipId: '9810123456789', chipProvider: 'Petlog', chipStatus: 'Registered', color: null, isVaccinated: null, isNeutered: null, isDewormed: null, adoptionDate: null, adopterId: null, adoptionFee: null, createdAt: '', updatedAt: '' },
  { id: 'mock-nje-3', shelterId: 'nje', name: 'Zeusz', species: 'Kutya', breed: 'Husky', age: 3, gender: 'Kan', weightKg: 22, traits: ['Energikus', 'Okos', 'Hűséges'], bio: 'Zeusz egy gyönyörű husky, aki imád futni. Aktív gazdit keres, aki tud vele napi szinten mozogni.', imageUrl: 'https://images.unsplash.com/photo-1605568427561-40dd23c2acea?auto=format&fit=crop&q=80&w=800', gallery: [], intakeDate: daysAgo(45), intakeType: 'Surrender', foundLocation: null, strayHoldEndDate: null, status: 'Available', microchipId: '9810987654321', chipProvider: null, chipStatus: 'Unregistered', color: null, isVaccinated: null, isNeutered: null, isDewormed: null, adoptionDate: null, adopterId: null, adoptionFee: null, createdAt: '', updatedAt: '' },
  { id: 'mock-nje-4', shelterId: 'nje', name: 'Liza', species: 'Kutya', breed: 'Labrador', age: 1, gender: 'Nőstény', weightKg: 18, traits: ['Játékos', 'Barátságos', 'Félénk'], bio: 'Liza egy fiatal, energikus labrador lány. Imád labdázni és pancsolni.', imageUrl: 'https://images.unsplash.com/photo-1605897472359-85e4b94d685d?auto=format&fit=crop&q=80&w=800', gallery: [], intakeDate: daysAgo(20), intakeType: 'Stray', foundLocation: null, strayHoldEndDate: null, status: 'Available', microchipId: null, chipProvider: null, chipStatus: null, color: null, isVaccinated: null, isNeutered: null, isDewormed: null, adoptionDate: null, adopterId: null, adoptionFee: null, createdAt: '', updatedAt: '' },
  { id: 'mock-nje-6', shelterId: 'nje', name: 'Kormos', species: 'Macska', breed: 'Bombay', age: 1, gender: 'Kan', weightKg: 4, traits: ['Kíváncsi', 'Játékos', 'Okos'], bio: 'Kormos egy fiatal, fekete kandúr. Nagyon kíváncsi természet, minden érdekli.', imageUrl: 'https://images.unsplash.com/photo-1543852786-1cf6624b9987?auto=format&fit=crop&q=80&w=800', gallery: [], intakeDate: daysAgo(60), intakeType: 'Stray', foundLocation: null, strayHoldEndDate: null, status: 'Available', microchipId: null, chipProvider: null, chipStatus: null, color: null, isVaccinated: null, isNeutered: null, isDewormed: null, adoptionDate: null, adopterId: null, adoptionFee: null, createdAt: '', updatedAt: '' },
  { id: 'mock-nje-8', shelterId: 'nje', name: 'Pöttyös', species: 'Kutya', breed: 'Dalmata', age: 2, gender: 'Kan', weightKg: 25, traits: ['Energikus', 'Okos', 'Barátságos'], bio: 'Pöttyös egy gyönyörű dalmata fiú, aki imád szaladni. Kertes házba ajánlott.', imageUrl: 'https://images.unsplash.com/photo-1627993073739-1229707e5968?auto=format&fit=crop&q=80&w=800', gallery: [], intakeDate: daysAgo(90), intakeType: 'Surrender', foundLocation: null, strayHoldEndDate: null, status: 'Available', microchipId: null, chipProvider: null, chipStatus: null, color: null, isVaccinated: null, isNeutered: null, isDewormed: null, adoptionDate: null, adopterId: null, adoptionFee: null, createdAt: '', updatedAt: '' },
];

const MOCK_SHELTERS: Shelter[] = [
  { id: 'nje', name: 'NJE Egyetemi Menhely', slug: 'nje-menhely', location: 'Kecskemét, Izsáki út 10.', lat: 46.8964, lng: 19.6897, capacity: 40, imageUrl: 'https://images.unsplash.com/photo-1548504769-900b70ed122e?auto=format&fit=crop&q=80&w=800', description: 'A Neumann János Egyetem mintamenhelye.', longStory: null, taxNumber: null, phone: null, email: null, website: null, isVerified: true, addressPlaceId: null, addressVerifiedAt: null, createdAt: '', updatedAt: '' },
  { id: 'bp_central', name: 'Budapest Központi Állatvédők', slug: 'budapest-kozponti', location: 'Budapest, XI. kerület', lat: 47.4768, lng: 19.0384, capacity: 120, imageUrl: 'https://images.unsplash.com/photo-1599443015574-be5fe8a05783?auto=format&fit=crop&q=80&w=800', description: 'A főváros legnagyobb menhelye.', longStory: null, taxNumber: null, phone: null, email: null, website: null, isVerified: true, addressPlaceId: null, addressVerifiedAt: null, createdAt: '', updatedAt: '' },
];

const MOCK_CAMPAIGNS: DonationCampaign[] = [
  { id: 'c1', title: 'Műláb Rokinak', description: 'Roki, a németjuhász balesetben elvesztette lábát. Speciális protézisre gyűjtünk.', targetAmount: 500000, raisedAmount: 320000, imageUrl: 'https://images.unsplash.com/photo-1529429617124-95b109e86bb8?auto=format&fit=crop&q=80&w=800', active: true, costBreakdown: [{ item: 'Karbon protézis', amount: 350000 }, { item: 'Fizioterápia', amount: 100000 }] },
  { id: 'c2', title: 'Téli Fűtéskorszerűsítés', description: 'A kennelsor fűtése elavult. Segíts, hogy ne fázzanak télen!', targetAmount: 1200000, raisedAmount: 450000, imageUrl: 'https://images.unsplash.com/photo-1504208434309-cb69f4fe52b0?auto=format&fit=crop&q=80&w=800', active: true, costBreakdown: [{ item: 'Kazán', amount: 800000 }, { item: 'Csőrendszer', amount: 250000 }], acceptsVolunteers: true },
];

const MOCK_NOTIFICATIONS: AppNotification[] = [
  { id: 'n1', category: 'PostAdoption', severity: 'High', title: 'Hiányzó 3 napos check-in', message: 'Buksi ellenőrzése nem történt meg.', timestamp: new Date().toISOString() },
  { id: 'n2', category: 'Campaign', severity: 'Medium', title: 'Kampány mérföldkő', message: 'A Műláb Rokinak kampány elérte az 50%-ot!', timestamp: new Date(Date.now() - 86400000).toISOString() },
];

const MOCK_TRANSACTIONS: FinancialTransaction[] = [
  { id: 't1', shelterId: 'nje', date: daysAgo(5), amount: 500000, type: 'Income', category: 'Donation', description: 'Mercedes-Benz támogatás', createdAt: '' },
  { id: 't2', shelterId: 'nje', date: daysAgo(2), amount: 120000, type: 'Expense', category: 'Donation', description: 'Tápbeszerzés', createdAt: '' },
  { id: 't3', shelterId: 'nje', date: daysAgo(10), amount: 15000, type: 'Income', category: 'Adoption', description: 'Buksi örökbefogadási díj', animalId: 'nje_1', createdAt: '' },
  { id: 't4', shelterId: 'nje', date: daysAgo(15), amount: 45000, type: 'Expense', category: 'Donation', description: 'Rex csípőröntgen', animalId: 'nje_7', createdAt: '' },
  { id: 't5', shelterId: 'nje', date: daysAgo(1), amount: 5000, type: 'Income', category: 'Subscription', description: 'Kiss Anna havi támogatás', isRecurring: true, createdAt: '' },
];

const MOCK_ADOPTED_ANIMALS: Animal[] = [
  { id: 'nje_1', shelterId: 'nje', name: 'Buksi', species: 'Kutya', breed: 'Golden Retriever', age: 4, gender: 'Kan', weightKg: null, traits: ['Barátságos'], bio: null, imageUrl: 'https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&q=80&w=800', gallery: [], intakeDate: daysAgo(200), intakeType: null, foundLocation: null, strayHoldEndDate: null, status: 'Adopted', microchipId: null, chipProvider: null, chipStatus: null, color: null, isVaccinated: null, isNeutered: null, isDewormed: null, adoptionDate: daysAgo(10), adopterId: null, adopterName: 'Kovács János', adoptionFee: null, createdAt: '', updatedAt: '', postAdoptionUpdates: [{ id: 'u1', updateType: '3_Day', createdAt: daysAgo(7), imageUrl: '', mood: 'Happy', notes: 'Jól beilleszkedett!', verifiedByShelter: true }] },
  { id: 'nje_10', shelterId: 'nje', name: 'Cirmi', species: 'Macska', breed: 'Cirmos', age: 4, gender: 'Nőstény', weightKg: null, traits: ['Játékos'], bio: null, imageUrl: 'https://images.unsplash.com/photo-1513360371669-4adf3dd7dff8?auto=format&fit=crop&q=80&w=800', gallery: [], intakeDate: daysAgo(180), intakeType: null, foundLocation: null, strayHoldEndDate: null, status: 'Adopted', microchipId: null, chipProvider: null, chipStatus: null, color: null, isVaccinated: null, isNeutered: null, isDewormed: null, adoptionDate: daysAgo(15), adopterId: null, adopterName: 'Nagy Anna', adoptionFee: null, createdAt: '', updatedAt: '', postAdoptionUpdates: [] },
];

// ═══════════════════════════════════════════════════════════════════════════
// Adoption Lead Hooks (for shelter review)
// ═══════════════════════════════════════════════════════════════════════════

import type { AdoptionLead } from './types';

/** Fetch pending adoption leads for a shelter. */
export function usePendingLeads(shelterId?: string): QueryState<AdoptionLead> & { refetch: () => void } {
  return useSupabaseQuery<AdoptionLead>(
    'adoption_leads',
    q => {
      // Filter by shelter via an inner join on animals; RLS additionally
      // restricts rows to the caller's own shelter. The !animal_id hint
      // disambiguates the two FK paths (animals.lead_id vs. leads.animal_id).
      let builder = q
        .select('*, animals!animal_id!inner(shelter_id)')
        .eq('status', 'Pending')
        .order('created_at', { ascending: false })
        .limit(50);
      if (shelterId) builder = builder.eq('animals.shelter_id', shelterId);
      return builder;
    },
    [],
    [shelterId]
  );
}

/** Fetch a single adoption lead by ID. */
export function useAdoptionLead(leadId: string | undefined): { lead: AdoptionLead | null; loading: boolean; error: string | null; refetch: () => void } {
  const [lead, setLead] = useState<AdoptionLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!leadId) { setLead(null); setLoading(false); return; }
    if (!isSupabaseConfigured()) { setLead(null); setLoading(false); return; }

    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('adoption_leads')
        .select('*')
        .eq('id', leadId)
        .maybeSingle();
      if (err) throw err;
      setLead(data ? toCamelCase<AdoptionLead>(data) : null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetch(); }, [fetch]);
  return { lead, loading, error, refetch: fetch };
}

/** Fetch a single lead by animal ID (for adopter to check adoption status). */
export function useAdoptionLeadByAnimal(animalId: string | undefined): { lead: AdoptionLead | null; loading: boolean; error: string | null; refetch: () => void } {
  const [lead, setLead] = useState<AdoptionLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!animalId) { setLead(null); setLoading(false); return; }
    if (!isSupabaseConfigured()) { setLead(null); setLoading(false); return; }

    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('adoption_leads')
        .select('*')
        .eq('animal_id', animalId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (err) throw err;
      setLead(data ? toCamelCase<AdoptionLead>(data) : null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => { fetch(); }, [fetch]);
  return { lead, loading, error, refetch: fetch };
}
